import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ConciergeChat — persistent bottom-right pill that expands into a
 * concierge-style chat panel. Non-AI: submits inquiries to
 * POST /api/concierge/message which stores them for the team to reply.
 *
 * A friendly auto-reply bubble appears after successful submission.
 */

const QUICK_REPLIES = [
  "What chamber size fits my treatment room?",
  "What are current financing terms?",
  "How long is installation?",
  "Do you ship to my state?",
];

const STORAGE_KEY = "tt-concierge-name";

export default function ConciergeChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      from: "concierge",
      text: "Hi — I'm Elena, TheraTec Concierge. Ask me anything about sizing, install, or financing. A real human replies within business hours.",
    },
  ]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      if (saved.name) setName(saved.name);
      if (saved.email) setEmail(saved.email);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sendMessage = async (text) => {
    const body = (text ?? input).trim();
    if (!body || sending) return;
    setSending(true);
    // Optimistic append
    setMessages((m) => [...m, { from: "you", text: body }]);
    setInput("");

    try {
      const res = await fetch(`${API}/api/concierge/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim() || null,
          email: email.trim() || null,
          message: body,
          page: typeof window !== "undefined" ? window.location.pathname : "/",
        }),
      });
      if (!res.ok) throw new Error("send-failed");
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ name: name.trim(), email: email.trim() }));
      } catch (_) {}
      setMessages((m) => [
        ...m,
        {
          from: "concierge",
          text: email.trim()
            ? `Got it — I'll reply to ${email.trim()} within business hours. In the meantime, our Hyperbaric guide covers most sizing questions.`
            : "Got it — leave your email below so I can follow up personally, or a member of our team will reach out via the site.",
        },
      ]);
      setSubmitted(true);
    } catch (_e) {
      setMessages((m) => [
        ...m,
        {
          from: "concierge",
          text: "Something went wrong sending that. Please try again in a moment or email concierge@theratec.com directly.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Pill trigger */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="concierge-pill"
          className="fixed z-[70] bottom-6 right-6 group flex items-center gap-3 pl-4 pr-5 py-3 rounded-full shadow-xl border transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl"
          style={{
            background: "#1C1C1C",
            color: "#FAF9F6",
            borderColor: "rgba(201,169,97,0.55)",
            boxShadow: "0 10px 30px rgba(28,28,28,0.28), 0 0 0 1px rgba(201,169,97,0.15)",
          }}
        >
          <span
            className="relative flex h-8 w-8 items-center justify-center rounded-full"
            style={{ background: "rgba(201,169,97,0.15)", border: "1px solid rgba(201,169,97,0.5)" }}
          >
            <MessageCircle size={15} strokeWidth={1.6} className="text-[#E8D6A8]" />
            <span
              aria-hidden
              className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full"
              style={{ background: "#C9A961", boxShadow: "0 0 8px rgba(201,169,97,0.9)" }}
            />
          </span>
          <span className="text-left leading-tight">
            <span className="block text-[10px] tracking-[0.18em] uppercase text-[#C9A961]">Concierge</span>
            <span className="block text-sm font-medium">Ask a question</span>
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          role="dialog"
          aria-label="TheraTec Concierge Chat"
          data-testid="concierge-panel"
          className="fixed z-[70] bottom-6 right-6 w-[92vw] sm:w-[380px] max-h-[80vh] flex flex-col border shadow-2xl overflow-hidden"
          style={{
            background: "#FAF9F6",
            borderColor: "rgba(201,169,97,0.35)",
            borderRadius: 4,
            animation: "tt-fade-up 340ms ease both",
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-5 py-4 border-b"
            style={{ background: "#1C1C1C", color: "#FAF9F6", borderColor: "rgba(201,169,97,0.25)" }}
          >
            <div className="flex items-center gap-3">
              <div
                className="h-9 w-9 rounded-full flex items-center justify-center text-[11px] tracking-[0.14em]"
                style={{ background: "rgba(201,169,97,0.18)", border: "1px solid rgba(201,169,97,0.5)", color: "#E8D6A8" }}
              >
                EL
              </div>
              <div className="leading-tight">
                <div className="text-[10px] tracking-[0.18em] uppercase text-[#C9A961]">TheraTec Concierge</div>
                <div className="text-sm">Elena · usually replies within 1 hour</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="concierge-close"
              aria-label="Close chat"
              className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
            >
              <X size={16} strokeWidth={1.6} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-5 space-y-3"
            style={{ background: "#FAF9F6" }}
          >
            {messages.map((m, idx) => (
              <div
                key={idx}
                data-testid={`concierge-msg-${m.from}-${idx}`}
                className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                  m.from === "you"
                    ? "ml-auto bg-[#1C1C1C] text-[#FAF9F6] rounded-[14px] rounded-br-[2px]"
                    : "mr-auto bg-white text-[#1C1C1C] border border-[#E5E2DA] rounded-[14px] rounded-bl-[2px]"
                }`}
              >
                {m.text}
              </div>
            ))}

            {/* Quick replies (only when just Elena has spoken) */}
            {messages.length === 1 && (
              <div className="pt-2 flex flex-wrap gap-2">
                {QUICK_REPLIES.map((q) => (
                  <button
                    key={q}
                    onClick={() => sendMessage(q)}
                    data-testid={`concierge-quick-${q.slice(0, 12)}`}
                    disabled={sending}
                    className="text-[12px] px-3 py-1.5 border border-[#C9A961]/50 text-[#1C1C1C] hover:bg-[#C9A961]/10 transition-colors rounded-full"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Identity + Composer */}
          <div className="border-t border-[#E5E2DA] p-3 bg-white space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="concierge-name"
                className="w-full text-sm px-3 py-2 border border-[#E5E2DA] focus:outline-none focus:border-[#C9A961] transition-colors bg-[#FAF9F6]"
              />
              <input
                type="email"
                placeholder="Email (for reply)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="concierge-email"
                className="w-full text-sm px-3 py-2 border border-[#E5E2DA] focus:outline-none focus:border-[#C9A961] transition-colors bg-[#FAF9F6]"
              />
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendMessage();
              }}
              className="flex items-center gap-2"
            >
              <input
                type="text"
                placeholder="Type your question…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="concierge-input"
                className="flex-1 text-sm px-3 py-2.5 border border-[#E5E2DA] focus:outline-none focus:border-[#C9A961] transition-colors bg-[#FAF9F6]"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid="concierge-send"
                className="h-[38px] w-[38px] flex items-center justify-center bg-[#1C1C1C] text-[#FAF9F6] hover:bg-[#C9A961] hover:text-[#1C1C1C] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={1.6} />}
              </button>
            </form>

            {submitted && (
              <div className="flex items-center gap-2 text-[11px] text-[#5C5C5C] pt-1">
                <CheckCircle2 size={12} className="text-[#C9A961]" />
                Message delivered to the concierge inbox.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
